import { useState, useRef, FormEvent, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
const RATIONALE_TAGS = [
    '실적 개선',
    '신규 사업',
    '밸류에이션',
    '모멘텀',
    '기술적 분석',
    '수급',
    '테마',
    '배당',
    '턴어라운드',
    '기타',
];

interface UploadedFile {
    file: File;
    preview: string;
    r2Key?: string;
    url?: string;
    uploading: boolean;
    error?: string;
}

export default function NewPosition() {
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Form state
    const [symbol, setSymbol] = useState('');
    // Auto-detect market based on symbol
    const market = /^[0-9]{6}$/.test(symbol) ? 'KRX' : 'US';
    const [fillType, setFillType] = useState<'BUY' | 'SELL'>('BUY');
    const [fillDate, setFillDate] = useState(new Date().toISOString().slice(0, 10));
    const [fillPrice, setFillPrice] = useState('');
    const [fillQty, setFillQty] = useState('');
    const [fillMemo, setFillMemo] = useState('');
    const [stopLossOverride, setStopLossOverride] = useState('');
    const [takeProfitPct, setTakeProfitPct] = useState('');
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [rationaleText, setRationaleText] = useState('');
    const [counterRationaleText, setCounterRationaleText] = useState('');
    const [files, setFiles] = useState<UploadedFile[]>([]);

    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    function toggleTag(tag: string) {
        setSelectedTags(prev =>
            prev.includes(tag)
                ? prev.filter(t => t !== tag)
                : [...prev, tag]
        );
    }

    // Image Compression Utility
    function compressImage(file: File, maxWidth: number = 1920, quality: number = 0.8): Promise<File> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target?.result as string;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    if (width > maxWidth) {
                        height = (height * maxWidth) / width;
                        width = maxWidth;
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx?.drawImage(img, 0, 0, width, height);

                    canvas.toBlob((blob) => {
                        if (!blob) {
                            reject(new Error('Canvas to Blob failed'));
                            return;
                        }
                        const compressedFile = new File([blob], file.name, {
                            type: file.type,
                            lastModified: Date.now(),
                        });
                        resolve(compressedFile);
                    }, file.type, quality);
                };
                img.onerror = (error) => reject(error);
            };
            reader.onerror = (error) => reject(error);
        });
    }

    async function handleFileSelect(e: ChangeEvent<HTMLInputElement>) {
        const selectedFiles = e.target.files;
        if (!selectedFiles) return;

        // Process files concurrently
        const filePromises = Array.from(selectedFiles).map(async (file) => {
            try {
                // Compress image
                const compressedFile = await compressImage(file);
                console.log(`Compressed ${file.name}: ${(file.size / 1024).toFixed(0)}KB -> ${(compressedFile.size / 1024).toFixed(0)}KB`);

                return {
                    file: compressedFile,
                    preview: URL.createObjectURL(compressedFile),
                    uploading: false,
                } as UploadedFile;
            } catch (err) {
                console.error('Compression failed, using original:', err);
                return {
                    file,
                    preview: URL.createObjectURL(file),
                    uploading: false,
                } as UploadedFile;
            }
        });

        const newFiles = await Promise.all(filePromises);
        setFiles(prev => [...prev, ...newFiles]);

        // Reset input so same file can be selected again if needed
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }

    function removeFile(index: number) {
        setFiles(prev => {
            const newFiles = [...prev];
            URL.revokeObjectURL(newFiles[index].preview);
            newFiles.splice(index, 1);
            return newFiles;
        });
    }

    async function uploadFile(index: number) {
        const file = files[index];
        if (file.uploading || file.r2Key) return;

        setFiles(prev => {
            const newFiles = [...prev];
            newFiles[index] = { ...newFiles[index], uploading: true, error: undefined };
            return newFiles;
        });

        try {
            const response = await api.uploadFile(file.file);

            if (!response.success) {
                throw new Error(response.error || '업로드 실패');
            }

            const { fileKey, fileUrl } = response.data;

            setFiles(prev => {
                const newFiles = [...prev];
                newFiles[index] = {
                    ...newFiles[index],
                    uploading: false,
                    r2Key: fileKey,
                    url: fileUrl
                };
                return newFiles;
            });
        } catch (e: any) {
            setFiles(prev => {
                const newFiles = [...prev];
                newFiles[index] = { ...newFiles[index], uploading: false, error: e.message || '업로드 실패' };
                return newFiles;
            });
        }
    }

    async function handleSubmit(e: FormEvent) {
        e.preventDefault();
        setError('');

        // Validation
        if (!symbol.trim()) {
            setError('종목코드를 입력해주세요');
            return;
        }

        if (!fillPrice || parseFloat(fillPrice) <= 0) {
            setError('가격을 입력해주세요');
            return;
        }

        if (!fillQty || parseInt(fillQty) <= 0) {
            setError('수량을 입력해주세요');
            return;
        }

        // Get uploaded files (optional)
        const uploadedFiles = files.filter(f => f.r2Key && f.url);

        setIsSubmitting(true);

        // Create position
        const positionData = {
            market,
            symbol: symbol.toUpperCase(),
            stopLossPctOverride: stopLossOverride ? parseFloat(stopLossOverride) : null,
            takeProfitPct: takeProfitPct ? parseFloat(takeProfitPct) : null,
            rationaleTags: selectedTags,
            rationaleText: rationaleText || undefined,
            counterRationaleText: counterRationaleText || undefined,
            initialFill: {
                type: fillType,
                // Convert date to KST midnight (00:00:00+09:00)
                at: new Date(fillDate + 'T00:00:00+09:00').toISOString(),
                price: parseFloat(fillPrice),
                qty: parseInt(fillQty),
                memo: fillMemo || undefined,
            },
        };

        console.log('Creating position with data:', positionData);
        const response = await api.createPosition(positionData);
        console.log('API response:', response);

        if (!response.success) {
            const errorMsg = 'error' in response ? response.error : '포트폴리오 등록에 실패했습니다';
            console.error('Position creation failed:', errorMsg);
            setError(errorMsg);
            setIsSubmitting(false);
            return;
        }

        const positionId = (response.data as any).id;

        // Register attachments
        for (const file of uploadedFiles) {
            await api.addAttachment(positionId, {
                r2Key: file.r2Key,
                url: file.url,
                type: 'execution',
                caption: file.file.name,
            });
        }

        navigate(`/positions/${positionId}`);
    }

    const allFilesUploaded = files.length > 0 && files.every(f => f.r2Key || f.error);

    return (
        <div>
            <div className="page-header">
                <h1 className="page-title">포트폴리오 등록</h1>
            </div>

            <form onSubmit={handleSubmit}>
                {error && (
                    <div className="card mb-lg" style={{ background: 'var(--color-danger-bg)', borderColor: 'var(--color-danger)' }}>
                        <p className="text-danger">{error}</p>
                    </div>
                )}

                {/* Basic Info */}
                <div className="card mb-lg">
                    <h3 className="card-title mb-md">기본 정보</h3>

                    <div className="grid grid-cols-2" style={{ gap: 'var(--space-md)' }}>


                        <div className="form-group">
                            <label className="form-label">종목코드 *</label>
                            <input
                                type="text"
                                className="form-input"
                                value={symbol}
                                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                                placeholder="예: 005930"
                                required
                            />
                        </div>
                    </div>
                </div>

                {/* Initial Fill */}
                <div className="card mb-lg">
                    <h3 className="card-title mb-md">첫 체결</h3>

                    <div className="grid grid-cols-2" style={{ gap: 'var(--space-md)' }}>
                        <div className="form-group">
                            <label className="form-label">유형</label>
                            <select
                                className="form-input form-select"
                                value={fillType}
                                onChange={(e) => setFillType(e.target.value as 'BUY' | 'SELL')}
                            >
                                <option value="BUY">매수</option>
                                <option value="SELL">매도</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label">체결일</label>
                            <input
                                type="date"
                                className="form-input"
                                value={fillDate}
                                onChange={(e) => setFillDate(e.target.value)}
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">가격 *</label>
                            <input
                                type="number"
                                className="form-input"
                                value={fillPrice}
                                onChange={(e) => setFillPrice(e.target.value)}
                                placeholder="0"
                                min="0"
                                step="any"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">수량 *</label>
                            <input
                                type="number"
                                className="form-input"
                                value={fillQty}
                                onChange={(e) => setFillQty(e.target.value)}
                                placeholder="0"
                                min="1"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">메모</label>
                        <input
                            type="text"
                            className="form-input"
                            value={fillMemo}
                            onChange={(e) => setFillMemo(e.target.value)}
                            placeholder="체결 관련 메모..."
                        />
                    </div>
                </div>

                {/* Stop Loss / Take Profit */}
                <div className="card mb-lg">
                    <h3 className="card-title mb-md">손절/익절 설정</h3>

                    <div className="grid grid-cols-2" style={{ gap: 'var(--space-md)' }}>
                        <div className="form-group">
                            <label className="form-label">손절 기준 (%, 기본값 -7%)</label>
                            <input
                                type="number"
                                className="form-input"
                                value={stopLossOverride}
                                onChange={(e) => setStopLossOverride(e.target.value)}
                                placeholder="-7"
                                max="0"
                                step="0.1"
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">목표 수익률 (%)</label>
                            <input
                                type="number"
                                className="form-input"
                                value={takeProfitPct}
                                onChange={(e) => setTakeProfitPct(e.target.value)}
                                placeholder="10"
                                min="0"
                                step="0.1"
                            />
                        </div>
                    </div>
                </div>

                {/* Rationale */}
                <div className="card mb-lg">
                    <h3 className="card-title mb-md">진입 근거</h3>

                    <div className="form-group">
                        <label className="form-label">태그</label>
                        <div className="flex gap-sm" style={{ flexWrap: 'wrap' }}>
                            {RATIONALE_TAGS.map(tag => (
                                <button
                                    key={tag}
                                    type="button"
                                    onClick={() => toggleTag(tag)}
                                    className={`badge ${selectedTags.includes(tag) ? 'badge-info' : 'badge-neutral'}`}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {tag}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">매수 이유</label>
                        <textarea
                            className="form-input form-textarea"
                            value={rationaleText}
                            onChange={(e) => setRationaleText(e.target.value)}
                            placeholder="왜 이 종목을 매수하려고 하나요?"
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">반대 시나리오</label>
                        <textarea
                            className="form-input form-textarea"
                            value={counterRationaleText}
                            onChange={(e) => setCounterRationaleText(e.target.value)}
                            placeholder="이 매수가 틀릴 수 있는 이유는?"
                        />
                    </div>
                </div>

                {/* Attachments */}
                <div className="card mb-lg">
                    <h3 className="card-title mb-md">
                        증빙 이미지
                        <span className="text-sm text-muted font-normal" style={{ marginLeft: '8px' }}>
                            (선택사항: 증권사 체결내역, 메모 등)
                        </span>
                    </h3>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />

                    <div
                        className="upload-zone mb-md"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <p>클릭하여 이미지 선택</p>
                        <p className="text-sm text-muted">JPEG, PNG, GIF, WebP 지원</p>
                    </div>

                    {files.length > 0 && (
                        <div className="image-gallery">
                            {files.map((file, idx) => (
                                <div key={idx} className="gallery-item" style={{ position: 'relative' }}>
                                    <img src={file.preview} alt={file.file.name} />
                                    <div className="gallery-item-overlay">
                                        {file.uploading ? (
                                            <div className="loading-spinner" style={{ width: 24, height: 24 }}></div>
                                        ) : file.r2Key ? (
                                            <span className="badge badge-success">✓</span>
                                        ) : file.error ? (
                                            <span className="badge badge-danger">!</span>
                                        ) : (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary"
                                                onClick={(e) => { e.stopPropagation(); uploadFile(idx); }}
                                            >
                                                업로드
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger"
                                            onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                                            style={{ marginLeft: '4px' }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {files.length > 0 && !allFilesUploaded && (
                        <button
                            type="button"
                            className="btn btn-secondary mt-md"
                            onClick={() => files.forEach((_, idx) => uploadFile(idx))}
                        >
                            전체 업로드
                        </button>
                    )}
                </div>

                {/* Submit */}
                <div className="flex gap-md">
                    <button
                        type="submit"
                        className="btn btn-primary btn-lg"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? '등록 중...' : '포트폴리오 등록'}
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary btn-lg"
                        onClick={() => navigate('/positions')}
                    >
                        취소
                    </button>
                </div>
            </form>
        </div>
    );
}
